/** Pure helpers for /topClients — unit-tested without Vue mount. */

export const CLIENT_CATEGORIES = ['top', 'key', 'regular']

export const CLIENT_CATEGORY_OPTIONS = [
  { value: 'top', label: 'ТОП' },
  { value: 'key', label: 'Ключевой' },
  { value: 'regular', label: 'Обычный' },
]

export function emptyTopClientForm() {
  return {
    name: '',
    type: '',
    is_top: false,
    category: 'regular',
    penalty_percent: 100,
    comment: '',
  }
}

/** Strict bool for select/API quirks — "false" must stay false. */
export function coerceTopFlag(value) {
  return value === true || value === 1 || value === '1' || value === 'true'
}

/** Category must be one of known values; legacy rows fall back to is_top flag. */
export function normalizeClientCategory(value, isTopFallback) {
  if (CLIENT_CATEGORIES.includes(value)) return value
  return coerceTopFlag(isTopFallback) ? 'top' : 'regular'
}

/** Penalty is an integer percent 0..100, default 100 (full fine). */
export function coercePenaltyPercent(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return 100
  return Math.min(100, Math.max(0, Math.round(num)))
}

export function normalizeTopClientForm(form) {
  const category = normalizeClientCategory(form?.category, form?.is_top)
  return {
    name: String(form?.name ?? '').trim(),
    type: String(form?.type ?? '').trim(),
    is_top: category === 'top',
    category,
    penalty_percent: coercePenaltyPercent(form?.penalty_percent),
    comment: String(form?.comment ?? '').trim().slice(0, 2000),
  }
}

export function validateTopClientForm(form) {
  const normalized = normalizeTopClientForm(form)
  if (!normalized.name) {
    return { ok: false, error: 'Укажите название клиента', form: normalized }
  }
  return { ok: true, error: null, form: normalized }
}

export function clientCategoryLabel(value) {
  return CLIENT_CATEGORY_OPTIONS.find((option) => option.value === value)?.label || '—'
}

/** Row-level category with legacy is_top fallback. */
export function clientCategoryOf(client) {
  return normalizeClientCategory(client?.category, client?.is_top)
}

export function topClientFormFromClient(client) {
  const category = normalizeClientCategory(client?.category, client?.is_top)
  return {
    name: String(client?.name ?? ''),
    type: String(client?.type ?? ''),
    is_top: category === 'top',
    category,
    penalty_percent: coercePenaltyPercent(client?.penalty_percent),
    comment: String(client?.comment ?? ''),
  }
}

export function formatClientUpdated(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  const pad = (part) => String(part).padStart(2, '0')
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function normalizeTopClientsItems(items) {
  return Array.isArray(items) ? items : []
}

export function topClientsFromListResponse(response) {
  if (!response || typeof response !== 'object') {
    return { items: [], currentPage: 1, totalPages: 1 }
  }
  return {
    items: normalizeTopClientsItems(response.topClients),
    currentPage: Number(response.current_page) > 0 ? Number(response.current_page) : 1,
    totalPages: Number(response.total_pages) > 0 ? Number(response.total_pages) : 1,
  }
}

/** Full-page loader must not cover the create form during mutate. */
export function shouldShowTopClientsLoader({ statusLoading, mutating }) {
  return Boolean(statusLoading && !mutating)
}

export function topClientCreateSucceeded(created) {
  return Boolean(created && created.id)
}

export function shouldClearFormAfterCreate(created) {
  return topClientCreateSucceeded(created)
}

export function canDeleteClient(client) {
  return Boolean(client && client.id)
}

export function canEditClient(client) {
  return Boolean(client && client.id)
}

export function buildDeleteConfirmMessage(client) {
  const label = client?.name || `#${client?.id ?? '?'}`
  return `Удалить клиента «${label}»?`
}

export function displayTopFlag(isTop) {
  return coerceTopFlag(isTop) ? 'ТОП' : '—'
}
